import { getInventoryVariantsListAsync } from 'src/redux/actions/omsPageActions'
import { formatOrderApiStatusLabel } from '../orders/omsOrdersStatusUtils'

export type InventoryStatus = 'AVAILABLE' | 'INSUFFICIENT_STOCK' | 'OUT_OF_STOCK' | 'NOT_FOUND' | 'LOADING'

const normalizeOrderStatusToken = (value: string | null | undefined): string =>
  (value || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_')

/** Draft/Open orders may be saved without allocatable stock (allocation can happen later). */
export const shouldEnforceInventoryOnSave = (
  orderStatusApiId: string | null | undefined
): boolean => {
  const normalized = normalizeOrderStatusToken(orderStatusApiId)
  if (!normalized) return true
  return normalized !== 'DRAFT' && normalized !== 'OPEN'
}

export const computeLineItemInventoryStatus = (
  availableQty: number | null | undefined,
  quantity: number | string
): InventoryStatus => {
  if (availableQty == null) return 'NOT_FOUND'
  const qty = Number(quantity) || 1
  if (availableQty <= 0) return 'OUT_OF_STOCK'
  if (availableQty < qty) return 'INSUFFICIENT_STOCK'
  return 'AVAILABLE'
}

export interface InventoryValidationResult {
  availableQty: number | null
  inventoryStatus: InventoryStatus
}

export const fetchStockForLineItems = async (
  dispatch: any,
  lineItems: any[], // We use any[] here to avoid circular dependency on LineItem, but it maps exactly
  warehouseId: string
): Promise<any[]> => {
  if (!lineItems.length || !warehouseId) return lineItems

  const promises = lineItems.map(async (item) => {
    // Only fetch if we have an SKU or variant ID selected
    if (!item.platform_product_variant && !item.sku) return item

    try {
      const res = await dispatch(
        getInventoryVariantsListAsync({
          page: 1,
          limit: 10,
          source_warehouse_id: warehouseId,
          destination_warehouse_id: warehouseId,
          search: item.sku || item.name || '',
        })
      ).unwrap()

      const data: any[] = res?.results?.data || res?.data?.results?.data || []
      const match = data.find(
        (d: any) =>
          (item.platform_product_variant && String(d.platform_product_variant) === String(item.platform_product_variant)) ||
          (item.platform_product_variant && String(d.id) === String(item.platform_product_variant)) ||
          (item.sku && d.sku === item.sku)
      )

      let availableQty: number | null = null
      let inventoryStatus: InventoryStatus = 'NOT_FOUND'

      if (match) {
        const warehouseQty = match.source_total_quantity ?? match.total_quantity ?? 0
        const allocatedQty = item.allocation_details?.net_allocated_quantity ?? item.allocation_details?.allocated_quantity ?? 0
        const effectiveQty = warehouseQty + allocatedQty

        availableQty = effectiveQty
        if (effectiveQty === 0) {
          inventoryStatus = 'OUT_OF_STOCK'
        } else if (effectiveQty < (Number(item.quantity) || 1)) {
          inventoryStatus = 'INSUFFICIENT_STOCK'
        } else {
          inventoryStatus = 'AVAILABLE'
        }
      }

      return {
        ...item,
        variantOption: item.variantOption ? {
          ...item.variantOption,
          availableQty,
          warehouseQty: match ? (match.source_total_quantity ?? match.total_quantity ?? 0) : undefined,
          allocatedQty: match ? (item.allocation_details?.net_allocated_quantity ?? item.allocation_details?.allocated_quantity ?? 0) : undefined
        } : null,
        inventoryStatus,
      }
    } catch (e) {
      console.error('Failed to fetch stock for line item:', item.id, e)
      // Fallback on error to avoid blocking completely, or set to NOT_FOUND
      return {
        ...item,
        inventoryStatus: 'NOT_FOUND', 
      }
    }
  })

  return Promise.all(promises)
}

const resolveOrderStatusLabelForMessage = (
  orderStatusApiId?: string | null,
  orderStatusLabel?: string | null
): string | null => {
  if (orderStatusLabel?.trim()) return orderStatusLabel.trim()
  const fromApi = formatOrderApiStatusLabel(orderStatusApiId)
  return fromApi === 'N/A' ? null : fromApi
}

const formatLineItemLabelForMessage = (item: any): string => {
  const name = `${item?.name ?? ''}`.trim()
  if (name) return name
  const sku = `${item?.sku ?? ''}`.trim()
  if (sku) return `SKU: ${sku}`
  return 'Line item'
}

const getLineItemOrderedQuantity = (item: any): number => {
  const qty = Number(item?.quantity)
  return Number.isFinite(qty) && qty > 0 ? qty : 1
}

const getLineItemAvailableQuantity = (item: any): number => {
  const available = item?.variantOption?.availableQty
  if (available == null || !Number.isFinite(Number(available))) return 0
  return Number(available)
}

export const buildInventorySaveErrorMessage = (
  item: any,
  inventoryStatus: InventoryStatus,
  orderStatusApiId?: string | null,
  orderStatusLabel?: string | null
): string => {
  const statusLabel = resolveOrderStatusLabelForMessage(orderStatusApiId, orderStatusLabel)
  const orderPhrase = statusLabel ? `Cannot save this ${statusLabel} order` : 'Cannot save this order'
  const itemLabel = formatLineItemLabelForMessage(item)
  const qty = getLineItemOrderedQuantity(item)
  const available = getLineItemAvailableQuantity(item)

  switch (inventoryStatus) {
    case 'NOT_FOUND':
      return `${orderPhrase}: "${itemLabel}" is not available in the selected warehouse. Choose another product or warehouse, or set order status to Draft/Open to save without allocation.`
    case 'OUT_OF_STOCK':
      return `${orderPhrase}: "${itemLabel}" is out of stock (0 available, ${qty} ordered). Reduce quantity, change warehouse, or set status to Draft/Open.`
    case 'INSUFFICIENT_STOCK':
      return `${orderPhrase}: "${itemLabel}" needs ${qty} but only ${available} available in the warehouse. Lower quantity, change warehouse, or use Draft/Open.`
    default:
      return `${orderPhrase}: please resolve inventory errors for "${itemLabel}".`
  }
}

export const validateInventoryForSave = (
  lineItems: any[],
  orderStatusApiId?: string | null,
  orderStatusLabel?: string | null
): string | null => {
  if (!shouldEnforceInventoryOnSave(orderStatusApiId)) return null

  for (const item of lineItems) {
    const status = item.inventoryStatus as InventoryStatus | undefined
    if (status === 'NOT_FOUND' || status === 'OUT_OF_STOCK' || status === 'INSUFFICIENT_STOCK') {
      return buildInventorySaveErrorMessage(item, status, orderStatusApiId, orderStatusLabel)
    }
  }
  return null
}
